const COUNT_DURATION_MS = 1300;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  );
}

function easeOutCubic(p: number): number {
  return 1 - Math.pow(1 - p, 3);
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat("en-US").format(Number(value) || 0);
}

export function formatEuro(v: number | null | undefined): string {
  if (v == null || isNaN(v)) return "—";
  return (
    "€" +
    new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(
      Math.round(v),
    )
  );
}

export function formatDecimal(v: number | null | undefined): string {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(v) || 0);
}

/**
 * Animate a numeric value from 0 to `target` using requestAnimationFrame.
 * Calls `setValue` with the current integer value on every frame.
 * Returns a cancel function.
 */
export function animateNumber(
  target: number,
  setValue: (v: number) => void,
): () => void {
  const safe = Math.max(0, Number(target) || 0);
  if (prefersReducedMotion() || safe <= 0) {
    setValue(safe);
    return () => undefined;
  }
  let raf = 0;
  let start: number | null = null;
  let cancelled = false;
  const tick = (ts: number) => {
    if (cancelled) return;
    if (start == null) start = ts;
    const p = Math.min(1, (ts - start) / COUNT_DURATION_MS);
    setValue(Math.round(safe * easeOutCubic(p)));
    if (p < 1) raf = requestAnimationFrame(tick);
    else setValue(safe);
  };
  raf = requestAnimationFrame(tick);
  return () => {
    cancelled = true;
    cancelAnimationFrame(raf);
  };
}
